import { useNostr } from '@nostrify/react';
import { useQuery } from '@tanstack/react-query';

import { ALBUM_KIND, parseAlbumEvent, type AlbumManifest } from '@/lib/album';
import { useCurrentUser } from '@/hooks/useCurrentUser';

/** Fetch a single album manifest by author + d-tag. */
export function useAlbum(pubkey: string | undefined, d: string | undefined) {
  const { nostr } = useNostr();
  return useQuery({
    queryKey: ['album', pubkey, d],
    queryFn: async (c) => {
      const events = await nostr.query(
        [{ kinds: [ALBUM_KIND], authors: [pubkey!], '#d': [d!], limit: 1 }],
        { signal: c.signal },
      );
      const manifest = events.map(parseAlbumEvent).find((m): m is AlbumManifest => Boolean(m));
      if (!manifest) throw new Error('Album not found on relays');
      return manifest;
    },
    enabled: Boolean(pubkey && d),
    retry: 1,
  });
}

/** List the logged-in user's top-level albums (latest version per d-tag). */
export function useMyAlbums() {
  const { nostr } = useNostr();
  const { user } = useCurrentUser();
  return useQuery({
    queryKey: ['my-albums', user?.pubkey],
    queryFn: async (c) => {
      const events = await nostr.query(
        [{ kinds: [ALBUM_KIND], authors: [user!.pubkey], limit: 200 }],
        { signal: c.signal },
      );
      const byId = new Map<string, AlbumManifest>();
      for (const e of events) {
        const m = parseAlbumEvent(e);
        if (!m || m.parent) continue;
        const existing = byId.get(m.d);
        if (!existing || existing.event.created_at < e.created_at) byId.set(m.d, m);
      }
      return [...byId.values()].sort((a, b) => b.event.created_at - a.event.created_at);
    },
    enabled: Boolean(user),
  });
}
